import { useCallback, useEffect, useRef, useState } from "react";
import { useIonViewWillEnter, useIonViewWillLeave } from "@ionic/react";
import { SiteItemModel } from "../models/site-item.model";
import { useUserContext } from "../../user/contexts/user.context";
import { SitesService } from "../../shared/services/sites.service";
import { useSitesCache } from "../../shared/hooks/use-sites-cache.hook";
import { addDebugLog } from "../../shared/hooks/use-debug-logs.hook";

const PAGE_SIZE = 20;

export const useSiteList = () => {
  const [siteList, fillSiteList] = useState<SiteItemModel[] | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState(""); // Le terme de recherche actif (après clic sur rechercher)
  const [annotatedAssetFilter, setAnnotatedAssetFilter] = useState<'ALL'|'ANNOTATED'>("ALL");
  const [loadCounter, setLoadCounter] = useState(0); // Compteur pour forcer le rafraîchissement de l'IonInfiniteScroll
  const { currentOrganization } = useUserContext();
  const { clearCache } = useSitesCache();
  
  // Log quand currentOrganization change
  useEffect(() => {
    addDebugLog('app', `[use-site-list] currentOrganization changed: ${currentOrganization?.code || 'null'}`);
  }, [currentOrganization]);
  
  // Utiliser des refs pour éviter les problèmes de closure dans useCallback
  const isLoadingRef = useRef(isLoading);
  const hasMoreRef = useRef(hasMore);
  const activeSearchTermRef = useRef(activeSearchTerm);
  const annotatedAssetFilterRef = useRef(annotatedAssetFilter);
  
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);
  
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  
  useEffect(() => {
    activeSearchTermRef.current = activeSearchTerm;
  }, [activeSearchTerm]);
  
  useEffect(() => {
    annotatedAssetFilterRef.current = annotatedAssetFilter;
  }, [annotatedAssetFilter]);

  // Fonction pour charger la première page
  const fetchSiteList = useCallback(async (search?: string | unknown, annotationFilter?: 'ALL' | 'ANNOTATED') => {
    // S'assurer que search est une string (ignorer les objets/événements)
    const searchString = typeof search === 'string' ? search : undefined;
    const searchDisplay = searchString || (search !== undefined ? '[non-string]' : 'none');
    addDebugLog('app', `[use-site-list] fetchSiteList called - org: ${currentOrganization?.code}, search: ${searchDisplay}, filter: ${annotationFilter || 'none'}`);
    fillSiteList(null);
    setCurrentPage(1);
    setHasMore(true);
    setIsLoading(true);
    const searchToUse = searchString !== undefined ? searchString : activeSearchTermRef.current;
    const filterToUse = annotationFilter !== undefined ? annotationFilter : annotatedAssetFilterRef.current;
    
    // Si on passe explicitement un search, on met à jour activeSearchTerm
    if (searchString !== undefined) {
      setActiveSearchTerm(searchString);
    }
    
    // Si on passe explicitement un filtre, on met à jour l'état
    if (annotationFilter !== undefined) {
      setAnnotatedAssetFilter(annotationFilter);
    }
    
    try {
      const orgaCode = currentOrganization?.code;
      if (!orgaCode) {
        // L'organisation n'est pas encore chargée, ne pas afficher d'erreur
        // Elle sera chargée par OrganizationUrlHandler
        addDebugLog('app', '[use-site-list] No organization code, returning empty list');
        fillSiteList([]);
        setHasMore(false);
        return;
      }
      const hasAnnotation = filterToUse === 'ANNOTATED';
      addDebugLog('app', `[use-site-list] Calling SitesService.getSites - org: ${orgaCode}, hasAnnotation: ${hasAnnotation}`);
      const sites = await SitesService.getSites(orgaCode, searchToUse || undefined, 1, PAGE_SIZE, hasAnnotation);
      addDebugLog('app', `[use-site-list] SitesService.getSites returned ${sites.length} site(s)`);
      fillSiteList(sites);
      // Si on reçoit exactement PAGE_SIZE éléments, il y a probablement une page suivante
      const hasMorePages = sites.length === PAGE_SIZE;
      setHasMore(hasMorePages);
      setCurrentPage(1);
      setLoadCounter(prev => prev + 1); // Incrémenter pour forcer le rafraîchissement de l'IonInfiniteScroll
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      addDebugLog('app', `[use-site-list] ERROR loading sites: ${errorMsg}`);
      console.error('[use-site-list] Erreur lors du chargement des sites:', error);
      fillSiteList([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization]);

  // Fonction pour charger la page suivante (infinite scroll)
  const loadMoreSites = useCallback(async () => {
    if (isLoadingRef.current || !hasMoreRef.current) {
      return;
    }
    
    setIsLoading(true);
    try {
      const orgaCode = currentOrganization?.code;
      if (!orgaCode) {
        console.error('Aucun code d\'organisation trouvé');
        setHasMore(false);
        return;
      }
      
      const nextPage = currentPage + 1;
      const searchToUse = activeSearchTermRef.current || undefined;
      const hasAnnotation = annotatedAssetFilterRef.current === 'ANNOTATED';
      const newSites = await SitesService.getSites(orgaCode, searchToUse, nextPage, PAGE_SIZE, hasAnnotation);
      
      // Concaténer les nouveaux sites avec les existants
      fillSiteList((prevSites) => {
        if (!prevSites) return newSites;
        return [...prevSites, ...newSites];
      });
      
      // Si on reçoit exactement PAGE_SIZE éléments, il y a probablement encore des pages
      const hasMorePages = newSites.length === PAGE_SIZE;
      setHasMore(hasMorePages);
      setCurrentPage(nextPage);
      setLoadCounter(prev => prev + 1); // Incrémenter pour forcer le rafraîchissement de l'IonInfiniteScroll
    } catch (error) {
      console.error('Erreur lors du chargement de sites supplémentaires:', error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization, currentPage]);

  // Fonction pour lancer une recherche (réinitialise la pagination)
  const handleSearch = useCallback(() => {
    fetchSiteList(searchTerm);
  }, [searchTerm, fetchSiteList]);

  // Fonction pour effacer la recherche
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    fetchSiteList('', annotatedAssetFilterRef.current);
  }, [fetchSiteList]);
  
  // Fonction pour changer le filtre d'annotation
  const handleAnnotationFilterChange = useCallback((filter: 'ALL' | 'ANNOTATED') => {
    fetchSiteList(activeSearchTermRef.current, filter);
  }, [fetchSiteList]);

  const isCurrentPage = useRef(false);

  useIonViewWillEnter(() => {
    // Marquer que nous sommes sur la page
    isCurrentPage.current = true;
    
    // Synchroniser le champ de recherche avec le terme de recherche actif
    setSearchTerm(activeSearchTermRef.current);
    
    // Recharger les sites avec le terme de recherche actif et le filtre (s'ils existent)
    // Cela permet de conserver la recherche et le filtre quand on revient sur la page
    if (currentOrganization?.code) {
      fetchSiteList(activeSearchTermRef.current, annotatedAssetFilterRef.current);
    }
  });

  useIonViewWillLeave(() => {
    isCurrentPage.current = false;
  });

  // Recharger les sites quand l'organisation change ou devient disponible
  useEffect(() => {
    if (currentOrganization?.code) {
      addDebugLog('app', `[use-site-list] Organization available/changed: ${currentOrganization.code}`);
      clearCache(); // Vider le cache pour forcer le rechargement
      // Ne pas réinitialiser la recherche ni le filtre, utiliser les valeurs actives existantes
      // Toujours recharger même si isCurrentPage.current est false, car on peut être sur la page
      addDebugLog('app', '[use-site-list] Calling fetchSiteList with organization');
      fetchSiteList(activeSearchTermRef.current, annotatedAssetFilterRef.current);
    } else {
      addDebugLog('app', '[use-site-list] No organization code available yet, waiting...');
      // Si pas d'organisation, vider la liste pour éviter d'afficher des sites d'une autre organisation
      fillSiteList([]);
      setHasMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.code]); // Utiliser currentOrganization?.code pour éviter les rechargements inutiles

  return {
    fetchSiteList,
    loadMoreSites,
    handleSearch,
    clearSearch,
    siteList,
    siteListFiltered: siteList || [], // Plus de filtrage local, retourner directement la liste
    searchTerm,
    setSearchTerm,
    activeSearchTerm,
    annotatedAssetFilter,
    handleAnnotationFilterChange,
    hasMore,
    isLoading,
    loadCounter,
  };
};
